/**
 * cancel_run tool - Cancel a running flow execution
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const cancelRunInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID)"),
  runId: z.string().describe("The run ID (GUID) to cancel"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
});

export type CancelRunInput = z.infer<typeof cancelRunInputSchema>;

export const cancelRunTool = {
  name: "cancel_run",
  description:
    "Cancel a currently running flow execution. Use this to stop a flow that is taking too long or running incorrectly.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID)",
      },
      runId: {
        type: "string",
        description: "The run ID (GUID) to cancel",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
    },
    required: ["flowId", "runId"],
  },
};

export async function handleCancelRun(
  api: FlowManagementApi,
  input: unknown
): Promise<string> {
  const parsed = cancelRunInputSchema.parse(input);

  logger.info(
    { flowId: parsed.flowId, runId: parsed.runId },
    "Executing cancel_run"
  );

  await api.cancelRun(parsed.flowId, parsed.runId, parsed.environment);

  return `Cancelled run ${parsed.runId} of flow ${parsed.flowId}. It shows as Cancelled in get_runs within a few seconds; actions already completed are not rolled back.`;
}
